import {
  AttachmentItem,
  ConfirmationDialog,
  DataGridLoading,
  DragAndDropArea,
  FormContent,
  FormDrawer,
  FormFooter,
  FormHeader,
  Snackbar,
} from "Common/Components";
import React from "react";
import { useReportBug } from "./Hooks";
import { UI_ELEMENTS } from "Common/Constants";
import { FormHelperText, Stack, TextField, Box } from "@mui/material";
import { ValidationSchema } from "./Validation";
import { getConfirmationContent, getInitialValues } from "./Functions";
import { Add } from "./Repository";
import { Formik, type FormikErrors } from "formik";
import type { IFormState } from "./Interfaces";
import type { IBug, IErrorDetail, IResponse } from "Common/Interfaces";
import { useCreateBugMutation } from "Services/Admin";

interface IReportBug {
  open: boolean;
  handleClose: () => void;
}

export const ReportBug: React.FC<IReportBug> = (props) => {
  const { open, handleClose } = props;

  const {
    uiElementState,
    isLoadingFileTypes,
    rejectedFiles,
    handleCloseUIElement,
    handleCancelConfirmationDialogOpen,
    handleConfirmationDialogEvent,
    handleFilesDropped,
    handleOpenSnackbar,
  } = useReportBug({
    handleClose,
    open,
  });

  const handleRemoveAttachment = (
    index: number,
    setFieldValue: (field: string, value: unknown, shouldValidate?: boolean) => Promise<void | FormikErrors<IFormState>>,
    values: IFormState,
  ) => {
    setFieldValue(
      "attachments",
      values.attachments.filter((_, i) => i !== index),
    );
  };

  const generateResponse = (response: IResponse<IBug>) => {
    if ("error" in response) {
      const error = response.error as IErrorDetail;
      handleOpenSnackbar(error.message, false);
    } else if ("data" in response) {
      handleOpenSnackbar(`Bug (Id: ${response.data.bugId}) reported successfully.`, true);
      handleClose();
    }
  };

  const [createBug] = useCreateBugMutation();

  return (
    <React.Fragment>
      <FormDrawer open={open}>
        <Formik<IFormState>
          enableReinitialize
          validateOnBlur
          validateOnChange
          validateOnMount
          initialValues={getInitialValues()}
          validationSchema={ValidationSchema}
          onSubmit={async (values, { setSubmitting }) => {
            const command = Add.call(values);

            await createBug(command).then((response) => generateResponse(response));

            setSubmitting(false);
          }}
        >
          {({ values, isSubmitting, errors, touched, setFieldValue, handleChange, handleSubmit, handleBlur }) => (
            <React.Fragment>
              <FormHeader title="Report a Bug" handleClose={handleCancelConfirmationDialogOpen} />
              <FormContent>
                <DataGridLoading isLoading={isLoadingFileTypes} sx={{ px: 0 }}>
                  <React.Fragment>
                    <Stack spacing={2}>
                      <TextField
                        id="title"
                        name="title"
                        label="Title"
                        required
                        value={values.title}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(touched.title && errors.title)}
                        helperText={touched.title && errors.title}
                        slotProps={{ htmlInput: { maxLength: 100 } }}
                      />

                      <TextField
                        id="description"
                        name="description"
                        label="Description"
                        multiline
                        rows={8}
                        required
                        value={values.description}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={Boolean(touched.description && errors.description)}
                        helperText={touched.description && errors.description}
                        slotProps={{ htmlInput: { maxLength: 4000 } }}
                      />

                      <Box>
                        <DragAndDropArea
                          onFilesDropped={(files: File[]) => {
                            const newFiles = handleFilesDropped(files, values.attachments);

                            if (newFiles.length > 0) {
                              setFieldValue("attachments", newFiles);
                            }
                          }}
                        />

                        {Boolean(errors.attachments) && (
                          <FormHelperText error>
                            {typeof errors.attachments === "string" ? errors.attachments : ""}
                          </FormHelperText>
                        )}
                      </Box>
                    </Stack>

                    <Stack spacing={2} sx={{ mt: 2 }}>
                      {values.attachments.map((file, index) => (
                        <AttachmentItem
                          key={file.name}
                          file={file}
                          handleRemoveAttachment={() => handleRemoveAttachment(index, setFieldValue, values)}
                        />
                      ))}
                    </Stack>
                  </React.Fragment>
                </DataGridLoading>
              </FormContent>
              <FormFooter
                buttons={[
                  {
                    label: "Cancel",
                    variant: "text",
                    onClick: handleCancelConfirmationDialogOpen,
                    disabled: isSubmitting,
                    hasGap: true,
                  },
                  {
                    label: "Save",
                    loadingButton: true,
                    isLoading: isSubmitting,
                    onClick: () => handleSubmit(),
                  },
                ]}
              />
            </React.Fragment>
          )}
        </Formik>
      </FormDrawer>

      <ConfirmationDialog
        open={
          uiElementState.activeElement === UI_ELEMENTS.INVALID_CONFIRMATION ||
          uiElementState.activeElement === UI_ELEMENTS.CANCEL_CONFIRMATION ||
          uiElementState.activeElement === UI_ELEMENTS.LIMIT_EXCEEDED_CONFIRMATION
        }
        dialogEvent={handleConfirmationDialogEvent}
        confirmationContent={getConfirmationContent({
          activeElement: uiElementState.activeElement,
          rejectedFiles,
        })}
      />

      <Snackbar
        open={uiElementState.activeElement === UI_ELEMENTS.SNACKBAR}
        handleClose={handleCloseUIElement}
        message={uiElementState.snackbarMessage}
        severity={uiElementState.snackbarSeverity}
      />
    </React.Fragment>
  );
};
